import { StyleSheet, css } from 'aphrodite'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import SkeletonLoader from '../components/SkeletonLoader'
import { useUser } from '../contexts/UserContext'
import { invoiceService } from '../services/invoiceService'
import type { Invoice, UpdateInvoiceData } from '../types/invoice'

const EditInvoicePage: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useUser()
  const [invoice, setInvoice] = useState<Invoice | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  // Editable form fields
  const [companyName, setCompanyName] = useState('')
  const [companyAddress, setCompanyAddress] = useState('')
  const [invoiceDate, setInvoiceDate] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [summaryDescription, setSummaryDescription] = useState('')
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState('')
  const [paymentTermsText, setPaymentTermsText] = useState('')
  const [howToPay, setHowToPay] = useState('')

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setIsLoading(true)
        const data = await invoiceService.get(Number(id))
        setInvoice(data)

        // Only creator can edit
        if (data.created_by?.id !== user?.id) {
          toast.error('You do not have permission to edit this invoice.')
          navigate(`/invoices/view/${id}`)
          return
        }

        // Only UNPAID invoices can be edited
        if (data.status !== 'UNPAID') {
          toast.error('Only unpaid invoices can be edited.')
          navigate(`/invoices/view/${id}`)
          return
        }

        // Populate form fields
        setCompanyName(data.company_name || '')
        setCompanyAddress(data.company_address || '')
        setInvoiceDate(data.invoice_date || '')
        setDueDate(data.due_date || '')
        setSummaryDescription(data.summary_description || '')
        setPurchaseOrderNumber(data.purchase_order_number || '')
        setPaymentTermsText(data.payment_terms_text || '')
        setHowToPay(data.how_to_pay || '')
      } catch (error) {
        console.error('Failed to load invoice:', error)
        toast.error('Failed to load invoice')
        navigate('/invoices/sent')
      } finally {
        setIsLoading(false)
      }
    }
    fetchInvoice()
  }, [id, user?.id, navigate])

  const handleSave = async () => {
    if (!invoice?.id) return
    setIsSaving(true)
    try {
      const updateData: UpdateInvoiceData = {
        company_name: companyName,
        company_address: companyAddress,
        invoice_date: invoiceDate,
        due_date: dueDate,
        summary_description: summaryDescription,
        purchase_order_number: purchaseOrderNumber,
        payment_terms_text: paymentTermsText,
        how_to_pay: howToPay,
      }
      await invoiceService.update(invoice.id, updateData)
      toast.success('Invoice updated successfully!')
      navigate(`/invoices/view/${invoice.id}`)
    } catch (error) {
      console.error('Failed to update invoice:', error)
      toast.error('Failed to update invoice. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading || !invoice) {
    return (
      <div className={css(styles.container)}>
        <div className={css(styles.card)}>
          <SkeletonLoader rows={6} columns={2} showHeader={true} />
        </div>
      </div>
    )
  }

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.card)}>
        <div className={css(styles.header)}>
          <h2 className={css(styles.title)}>
            Edit Invoice #{invoice.invoice_number}
          </h2>
          <button
            className={css(styles.cancelButton)}
            onClick={() => navigate(`/invoices/view/${invoice.id}`)}
          >
            Cancel
          </button>
        </div>

        <div className={css(styles.formGrid)}>
          <div className={css(styles.fieldGroup)}>
            <label className={css(styles.label)}>Company Name</label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className={css(styles.input)}
            />
          </div>
          <div className={css(styles.fieldGroup)}>
            <label className={css(styles.label)}>Company Address</label>
            <input
              type="text"
              value={companyAddress}
              onChange={(e) => setCompanyAddress(e.target.value)}
              className={css(styles.input)}
            />
          </div>
          <div className={css(styles.fieldGroup)}>
            <label className={css(styles.label)}>Invoice Date</label>
            <input
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              className={css(styles.input)}
            />
          </div>
          <div className={css(styles.fieldGroup)}>
            <label className={css(styles.label)}>Due Date</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className={css(styles.input)}
            />
          </div>
          <div className={css(styles.fieldGroup)}>
            <label className={css(styles.label)}>PO Number</label>
            <input
              type="text"
              value={purchaseOrderNumber}
              onChange={(e) => setPurchaseOrderNumber(e.target.value)}
              className={css(styles.input)}
              placeholder="Purchase order number"
            />
          </div>
          <div className={css(styles.fieldGroup)}>
            <label className={css(styles.label)}>Payment Terms</label>
            <input
              type="text"
              value={paymentTermsText}
              onChange={(e) => setPaymentTermsText(e.target.value)}
              className={css(styles.input)}
              placeholder="e.g., Net 30, Due on receipt"
            />
          </div>
          <div className={css(styles.fieldGroupFull)}>
            <label className={css(styles.label)}>Summary Description</label>
            <textarea
              value={summaryDescription}
              onChange={(e) => setSummaryDescription(e.target.value)}
              className={css(styles.textarea)}
              placeholder="Brief description of work performed"
              rows={3}
            />
          </div>
          <div className={css(styles.fieldGroupFull)}>
            <label className={css(styles.label)}>How to Pay</label>
            <textarea
              value={howToPay}
              onChange={(e) => setHowToPay(e.target.value)}
              className={css(styles.textarea)}
              placeholder="Payment instructions (e.g., bank details, check address)"
              rows={3}
            />
          </div>
        </div>

        <div className={css(styles.actions)}>
          <button
            className={css(styles.cancelButton)}
            onClick={() => navigate(`/invoices/view/${invoice.id}`)}
          >
            Cancel
          </button>
          <button
            className={css(styles.saveButton)}
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <i className="fa-solid fa-spinner fa-spin" /> Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    padding: '24px',
    backgroundColor: '#F9FAFB',
    minHeight: '100vh',
  },
  card: {
    backgroundColor: 'white',
    padding: '32px',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    maxWidth: '800px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 600,
    color: '#111827',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    marginBottom: '32px',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  fieldGroupFull: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    gridColumn: '1 / -1',
  },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box',
    ':focus': {
      outline: 'none',
      borderColor: '#1C64F2',
      boxShadow: '0 0 0 2px rgba(28, 100, 242, 0.1)',
    },
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
    boxSizing: 'border-box',
    ':focus': {
      outline: 'none',
      borderColor: '#1C64F2',
      boxShadow: '0 0 0 2px rgba(28, 100, 242, 0.1)',
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  },
  cancelButton: {
    backgroundColor: '#F3F4F6',
    color: '#374151',
    padding: '10px 20px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#E5E7EB',
    },
  },
  saveButton: {
    backgroundColor: '#1C64F2',
    color: 'white',
    padding: '10px 24px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    ':hover': {
      backgroundColor: '#1955CC',
    },
    ':disabled': {
      backgroundColor: '#93C5FD',
      cursor: 'not-allowed',
    },
  },
})

export default EditInvoicePage
